import { useMemo } from "react";
import { TTypedTFunction, useTranslation } from "@lib/useTypedTranslation";
import { IFilterResult } from "@components/filters/filterLib";
import { getAllTranslatedAlertStatusFilterValues, groupFilterValues, homeLocationFilterValues, statusFilterValues } from "@components/filters/devicesFilterLib";
import { useGenericFilterOptions } from "../../../../../components/data-table/filters/useGenericFilterOptions";
import { omitShallow } from "@lib/omitShallow";
import { ITranslationKeys } from "@components/i18n/keys";
import { getGroups, getHomeLocations, getManufacturers, getModels, IGroup, IGroups, IHomeLocation } from "../../../../../services/config/config";

export type DeviceFilterData = [IGroups, string[], string[], IHomeLocation[]];

const deviceListFilters = ['manufacturers', 'models', 'groups', 'homeLocations', 'status', 'alertStatus'] as const;
type DevicesListFilter = typeof deviceListFilters[number];

function getInitialAvailableDeviceFilters(hiddenFilters: DevicesListFilter[]): IFilterResult {
  const initialAvailableFilters: IFilterResult = {
    manufacturers: [],
    models: [],
    groups: [],
    homeLocations: [],
    status: [],
    alertStatus: []
  };

  return omitShallow(initialAvailableFilters, ...hiddenFilters);
}

function createDeviceFilterProcessor(
  t: TTypedTFunction
) {
  return (data: DeviceFilterData, hiddenFilters: string[]): IFilterResult => {
    const homeLocationFilterVisible = !hiddenFilters.includes('homeLocations');
    const translatedStatusValues = statusFilterValues.map(val => ({
      ...val,
      value: t(val.value as keyof ITranslationKeys['translation'], { ns: 'translation' })
    }));

    const translatedGroupFilterValues = groupFilterValues.map(val => ({
      ...val,
      value: t(val.value as keyof ITranslationKeys['translation'], { ns: 'translation' })
    }));

    const translatedHomeLocationFilterValues = homeLocationFilterValues.map(val => ({
      ...val,
      value: t(val.value as keyof ITranslationKeys['translation'], { ns: 'translation' })
    }));

    const currentTranslatedAlertStatusValues = getAllTranslatedAlertStatusFilterValues(t);

    const groupsRaw = data[0];
    const models = data[1];
    const manufacturers = data[2];

    const groups = [
      ...translatedGroupFilterValues,
      ...(groupsRaw?.groups?.nodes ?? []).map((o: IGroup) => o.name)
    ];

    const availableFilters: IFilterResult = {
      groups,
      models,
      manufacturers,
      status: translatedStatusValues,
      alertStatus: currentTranslatedAlertStatusValues
    };

    if (homeLocationFilterVisible) {
      const homeLocationsRaw = data[3];
      const homeLocations = homeLocationsRaw.map((o) => ({ id: o.id, value: o.name }));
      availableFilters.homeLocations = [...translatedHomeLocationFilterValues, ...homeLocations];
    }

    return availableFilters;
  };
}

export const useDeviceFilterOptions = (
  hiddenFilters: DevicesListFilter[],
) => {
  const { t } = useTranslation(['translation', 'filters']);

  const homeLocationFilterVisible = useMemo(() => {
    return !hiddenFilters.includes('homeLocations');
  }, [hiddenFilters]);

  const translatedAlertStatusValues = useMemo(() => {
    return getAllTranslatedAlertStatusFilterValues(t);
  }, [t]);

  const dataProcessor = useMemo(() => {
    return createDeviceFilterProcessor(t);
  }, [t]);

  const dataFetchers = useMemo(() => {
    const fetchers: any[] = [
      getGroups,
      getModels,
      getManufacturers,
    ];
    if (homeLocationFilterVisible) {
      fetchers.push(getHomeLocations);
    }
    return fetchers;
  }, [homeLocationFilterVisible]);

  const { availableFilters, filtersLoaded } = useGenericFilterOptions({
    hiddenFilters,
    dataFetchers,
    dataProcessor,
    getInitialFilters: getInitialAvailableDeviceFilters
  });

  return {
    availableFilters,
    filtersLoaded,
    translatedAlertStatusValues
  };
};
